import { getOrRefreshToken } from "./api/api-token";
import {
  Api,
  ApiFallback,
  CSP,
  ENCRYPT_STORAGE,
  GET_API_MODE,
  PRIMARY_API_QUERY,
  RESOLVE_ENVIRONMENT_FROM_API_MODE,
} from "../config/config";
import { ToastMessage } from "../components/toast-notification/toast";
import { STORE_CSP } from "../database/store-names";
import { PT_STARTUP } from "../routes/stack/paths/pt-home";

const FAILOVER_STATE_KEY = "ApiFailoverState";
const FALLBACK_HOLD_MS = 15 * 60 * 1000;
const API_RECOVERY_RETRY_MS = 5000;
const API_RECOVERY_TOAST_COOLDOWN_MS = 4000;

let apiRecoveryTimer = null;
let apiRecoveryRequest = null;
let apiRecoveryInFlight = false;
let lastApiRecoveryToastAt = 0;

const getFailoverUntil = () => {
  try {
    const raw = window.localStorage.getItem(FAILOVER_STATE_KEY);
    if (!raw) return 0;
    const parsed = JSON.parse(raw);
    return Number(parsed?.fallbackUntil) || 0;
  } catch (_error) {
    return 0;
  }
};

const setFallbackHold = () => {
  try {
    window.localStorage.setItem(
      FAILOVER_STATE_KEY,
      JSON.stringify({ fallbackUntil: Date.now() + FALLBACK_HOLD_MS }),
    );
  } catch (_error) {
    // ignore storage failures
  }
};

const clearFallbackHold = () => {
  try {
    window.localStorage.removeItem(FAILOVER_STATE_KEY);
  } catch (_error) {
    // ignore storage failures
  }
};

const buildPrimaryUrl = (url) => {
  if (!url || !Api || !url.startsWith(Api)) {
    return url;
  }
  if (PRIMARY_API_QUERY === "" || url.includes("ev=") || url.includes("env=")) {
    return url;
  }
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}${PRIMARY_API_QUERY}`;
};

const buildFallbackUrl = (url) => {
  if (!url || !Api || !ApiFallback || !url.startsWith(Api)) {
    return undefined;
  }
  return `${ApiFallback}${url.slice(Api.length)}`;
};

const parseResponse = async (response) => {
  try {
    const raw = await response.text();
    try {
      return { data: JSON.parse(raw) };
    } catch (jsonError) {
      return { data: raw, parseError: jsonError };
    }
  } catch (jsonError) {
    return {
      parseError: jsonError,
      message: jsonError?.message || "Unable to parse server response.",
    };
  }
};

const buildErrorPayload = (error) =>
  JSON.stringify({
    Result: "ERROR",
    Message: error?.message || "Request failed.",
  });

const getStartupPath = () => {
  const storedCsp = ENCRYPT_STORAGE.getItem(STORE_CSP) || CSP;
  if (!storedCsp) {
    return null;
  }
  return PT_STARTUP.replace(":csp", String(storedCsp).toLowerCase());
};

const navigateToStartup = () => {
  const startupPath = getStartupPath();
  if (!startupPath) {
    return;
  }

  if (window.location.pathname !== startupPath) {
    window.history.replaceState({}, "", startupPath);
    window.dispatchEvent(new PopStateEvent("popstate"));
  }
};

const showApiRecoveryToast = async () => {
  if (Date.now() - lastApiRecoveryToastAt < API_RECOVERY_TOAST_COOLDOWN_MS) {
    return;
  }

  lastApiRecoveryToastAt = Date.now();
  await ToastMessage(1, "Unable to connect to server. Please try again.");
};

const clearApiRecoveryLoop = () => {
  if (apiRecoveryTimer) {
    window.clearTimeout(apiRecoveryTimer);
    apiRecoveryTimer = null;
  }
  apiRecoveryRequest = null;
  apiRecoveryInFlight = false;
};

const scheduleApiRecoveryLoop = () => {
  if (!apiRecoveryRequest || apiRecoveryTimer) {
    return;
  }

  apiRecoveryTimer = window.setTimeout(async () => {
    apiRecoveryTimer = null;

    if (!apiRecoveryRequest || apiRecoveryInFlight) {
      scheduleApiRecoveryLoop();
      return;
    }

    apiRecoveryInFlight = true;
    try {
      await apiRecoveryRequest();
      clearApiRecoveryLoop();
      if (window.location.pathname === getStartupPath()) {
        window.location.reload();
      }
      return;
    } catch (error) {
      console.error("API recovery retry failed:", error);
      apiRecoveryInFlight = false;
      scheduleApiRecoveryLoop();
      return;
    }
  }, API_RECOVERY_RETRY_MS);
};

const handleApiRecovery = async (error, retryRequest) => {
  apiRecoveryRequest = retryRequest;
  await showApiRecoveryToast();
  navigateToStartup();
  scheduleApiRecoveryLoop();
  return buildErrorPayload(error);
};

export const triggerApiRecoveryRedirect = async (error, retryRequest) => {
  const recoveryError =
    error instanceof Error
      ? error
      : new Error(String(error || "Request failed."));
  recoveryError.isApiRecoveryRedirect = true;
  await handleApiRecovery(recoveryError, retryRequest);
  throw recoveryError;
};

const executeWithFailover = async (primaryUrl, fallbackUrl, runRequest) => {
  const holdUntil = getFailoverUntil();
  const shouldUseFallbackFirst =
    fallbackUrl && fallbackUrl !== primaryUrl && Date.now() < holdUntil;
  const firstUrl = shouldUseFallbackFirst ? fallbackUrl : primaryUrl;
  const secondUrl = shouldUseFallbackFirst ? primaryUrl : fallbackUrl;

  try {
    const response = await runRequest(firstUrl);
    if (firstUrl === primaryUrl) {
      clearFallbackHold();
    }
    return response;
  } catch (error) {
    if (secondUrl && secondUrl !== firstUrl) {
      try {
        console.warn("API failed. Trying alternate API:", secondUrl);
        const response = await runRequest(secondUrl);
        if (firstUrl === primaryUrl && secondUrl === fallbackUrl) {
          setFallbackHold();
        } else if (secondUrl === primaryUrl) {
          clearFallbackHold();
        }
        return response;
      } catch (fallbackError) {
        console.error(fallbackError);
        throw fallbackError;
      }
    }
    console.error(error);
    throw error;
  }
};

export const postMethod = async (url, Object) => {
  const primaryUrl = buildPrimaryUrl(url);
  const fallbackUrl = buildFallbackUrl(url);
  console.log(primaryUrl, Object);

  const runRequest = async (requestUrl) => {
    // const token = await getOrRefreshToken();
    let response = await fetch(requestUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        // "Access-Control-Allow-Origin": "*",
        "Diatonn-Project": CSP,
        // "X-Access-Token": token,
        Environment: RESOLVE_ENVIRONMENT_FROM_API_MODE(GET_API_MODE()),
      },
      body: JSON.stringify(Object),
    });

    let rtResponse = await parseResponse(response);
    if (response.status !== 200) {
      throw Error(rtResponse.data?.message || "Request failed.");
    }

    return rtResponse.data;
  };

  try {
    return await executeWithFailover(primaryUrl, fallbackUrl, runRequest);
  } catch (error) {
    return await handleApiRecovery(error, () =>
      executeWithFailover(primaryUrl, fallbackUrl, runRequest),
    );
  }
};

export const postOuth = async (url) => {
  try {
    let response = await fetch(url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        // "Access-Control-Allow-Origin": "*",
      },
    });

    let rtResponse = await response.json();
    if (response.status !== 200) throw Error(rtResponse.message);

    return rtResponse;
  } catch (error) {
    // console.error(error);
  }
};

export const getMethod = async (url) => {
  const primaryUrl = buildPrimaryUrl(url);
  const fallbackUrl = buildFallbackUrl(url);

  const runRequest = async (requestUrl) => {
    let response = await fetch(requestUrl);
    let rtResponse = await parseResponse(response);
    if (response.status !== 200) {
      throw Error(rtResponse.data?.message || "Request failed.");
    }
    return rtResponse.data;
  };

  try {
    return await executeWithFailover(primaryUrl, fallbackUrl, runRequest);
  } catch (error) {
    return await handleApiRecovery(error, () =>
      executeWithFailover(primaryUrl, fallbackUrl, runRequest),
    );
  }
};
// export const postMethod = async (url, Object) => {
//   console.log(url, Object);
//   const options = {
//     method: "POST",
//     headers: {
//       Accept: "application/json",
//       "Content-Type": "application/json",
//       "Access-Control-Allow-Origin": "*",
//     },
//     body: Object,
//   };
//   const { timeout = 8000 } = options;
//   const controller = new AbortController();
//   const id = setTimeout(() => controller.abort(), timeout);
//   try {
//     let response = await fetch(url, { ...options, signal: controller.signal });

//     let rtResponse = await response.json();
//     if (response.status !== 200) {
//       return rtResponse.message;
//     }
//     clearTimeout(id);
//     return rtResponse;
//   } catch (error) {
//     clearTimeout(id);
//     return error;
//   }
// };
